import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Key, Eye, FileText, Lock, 
  CheckCircle, Users, Clock, Database
} from 'lucide-react';

const principalChain = [
  { role: 'User', value: 'admin@acme.com', desc: '(approved)' },
  { role: 'Agent', value: 'identity_agent', desc: '' },
  { role: 'Connector', value: 'okta_connector', desc: '' },
  { role: 'Service Account', value: 'vedaya-okta-service@acme.okta.com', desc: '' }
];

const scopedTokenExample = {
  scope: 'Add mlee@acme.com to prod-db-admin group',
  valid: '2026-01-15 10:00 to 18:00 UTC',
  maxUses: '1',
  environment: 'Production',
  approvers: 'Mary Jones (Manager), Security Team'
};

const auditItems = [
  'Request and response payloads (secrets redacted)',
  'Decision reasoning from agent',
  'Policy evaluation results',
  'Verification outcomes',
  'Rollback triggers and results'
];

const dataHandling = [
  'Vedaya processes data in transit, does not store PII at rest',
  'Evidence bundles contain hashes and references, not raw data',
  'Credentials stored in customer-managed vaults',
  'SOC 2 Type II certified',
  'GDPR and CCPA compliant'
];

const syncTargets = [
  'Tickets created/updated in Jira, Zendesk, Freshservice',
  'Audit events sent to your SIEM',
  'Status synced to HRIS (where supported)',
  'Evidence bundles attached to tickets'
];

export const SecurityInfo = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="security-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#4a70ff] flex items-center justify-center">
                <span className="text-white font-bold text-xs">V</span>
              </div>
              <span className="text-base font-semibold text-black">Vedaya</span>
            </Link>
            <Link
              to="/control-tower"
              className="px-4 py-2 text-sm font-medium text-white bg-[#4a70ff] rounded-lg hover:bg-[#3a5fe6] transition-colors"
            >
              Open Demo
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 bg-gray-50 border-b border-gray-200">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl md:text-3xl font-semibold text-black mb-4">
            Safety through evidence, not hope.
          </h1>
          <p className="text-lg text-gray-600">
            Delegated identity. Scoped tokens. Policy gates. Full audit trail.
          </p>
        </div>
      </section>

      {/* Delegated Identity */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#4a70ff]" />
                Delegated Identity
              </h2>
              <p className="text-gray-600 mb-4">
                Every action in Vedaya carries a principal chain. Know exactly who or what performed each action.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><strong>Initiating user:</strong> The human who requested or approved</li>
                <li><strong>Agent:</strong> The Vedaya agent that planned and executed</li>
                <li><strong>Connector:</strong> The integration that performed the API call</li>
                <li><strong>Service account:</strong> The credential used in the target system</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500 mb-3">Example principal chain</div>
              <div className="bg-white rounded-lg border border-gray-100 p-4 font-mono text-xs">
                <div className="text-gray-500 mb-2">Action: Create user jsmith@acme.com in Okta</div>
                <div className="space-y-1">
                  {principalChain.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-gray-400 w-24">{p.role}:</span>
                      <span className="text-gray-900">{p.value}</span>
                      <span className="text-gray-400">{p.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scoped Tokens */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500 mb-3">Example scoped token</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Scope:</span>
                  <span className="text-gray-900 text-right">{scopedTokenExample.scope}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Valid:</span>
                  <span className="text-gray-900">{scopedTokenExample.valid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Max uses:</span>
                  <span className="text-gray-900">{scopedTokenExample.maxUses}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Environment:</span>
                  <span className="text-gray-900">{scopedTokenExample.environment}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Approvers:</span>
                  <span className="text-gray-900 text-right">{scopedTokenExample.approvers}</span>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
                <Key className="w-5 h-5 text-[#4a70ff]" />
                Scoped Tokens, Not Standing Access
              </h2>
              <p className="text-gray-600 mb-4">
                Vedaya uses scoped approval tokens for every high-risk action:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-gray-700">Time-bound (expires after N hours)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-gray-700">Resource-bound (only specified targets)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-gray-700">Use-limited (single use or N uses max)</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span className="text-gray-700">Environment-scoped (prod, staging, dev)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Audit Logs */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-black mb-6 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[#4a70ff]" />
            Audit Logs
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3">What we log</h3>
              <ul className="space-y-2">
                {auditItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#4a70ff] mt-0.5 shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Retention and format</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700"><strong>Retention:</strong> 7 years standard. 10 years available.</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700"><strong>Format:</strong> JSON-structured, exportable, SIEM-compatible</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Handling */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-black mb-6 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#4a70ff]" />
            Data Handling
          </h2>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Your data stays yours</h3>
            <ul className="space-y-2">
              {dataHandling.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Shield className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Coexistence */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-black mb-3 flex items-center gap-2">
            <Database className="w-5 h-5 text-[#4a70ff]" />
            Sync Back to Your Existing Tools
          </h2>
          <p className="text-gray-600 mb-6">
            Vedaya projects outcomes to your existing systems. Your existing tools remain the system of record for compliance.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {syncTargets.map((item, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-[#4a70ff] mt-0.5 shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#4a70ff]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Questions about security?</h2>
          <p className="text-white/80 mb-6">
            See how evidence, policy gates, and audit trails work in the demo.
          </p>
          <Link
            to="/control-tower"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#4a70ff] font-medium rounded-lg hover:bg-gray-100 transition-colors"
          >
            Open Control Tower Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[#4a70ff] flex items-center justify-center">
              <span className="text-white font-bold text-xs">V</span>
            </div>
            <span className="text-sm text-gray-500">Vedaya AI</span>
          </div>
          <p className="text-sm text-gray-400">2026 Vedaya AI</p>
        </div>
      </footer>
    </div>
  );
};

export default SecurityInfo;
